import {
    buildCapabilityFinancePartnerDisplayNames,
    filterFinancePartnerMasterListByAllowList,
} from './capabilityService';
import designServices from './designServices';
import hoverServices from './hoverServices';
import masterServices from './masterServices';
import opportunityInfoService, {
    OpportunityDetailNotFoundError,
} from './opportunityInfoService';
import quoteServices from './quoteServices';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../store/oneButtonStore';
import { useViewerStore } from '../../store/store';
import {
    mergeHoverEstimateIntoPriceQuote,
    mergeHoverEstimateIntoPriceQuoteOverrides,
} from './applyHoverEstimateConvert';

const SITE_READY_POLL_MS = 200;
const SITE_READY_TIMEOUT_MS = 120_000;

export interface RunOpportunityInitializationOptions {
    salesForceId: string;
    requestId: number;
    /** When false, skip waiting for oneDRAW design + shade (e.g. batch / tests). */
    waitForSiteDesign?: boolean;
}

function isStale(requestId: number): boolean {
    return useOneButtonStore.getState().opportunityInitRequestId !== requestId;
}

function setPhase(requestId: number, phase: string): void {
    if (isStale(requestId)) return;
    useOneButtonStore.setState({ opportunityInitializationPhase: phase });
}

async function waitForSiteDesignReady(requestId: number): Promise<void> {
    const start = Date.now();
    setPhase(requestId, 'Loading site design and calculating shade…');
    while (Date.now() - start < SITE_READY_TIMEOUT_MS) {
        if (isStale(requestId)) return;
        const { loadingCompleted, refreshingShade, siteLoadingError } = useViewerStore.getState();
        // Site load failed — do not wait for 3D design/shade; continue dashboard API loading.
        if (siteLoadingError?.trim()) return;
        if (loadingCompleted && !refreshingShade) return;
        await new Promise((resolve) => setTimeout(resolve, SITE_READY_POLL_MS));
    }
}

async function loadMasterDataForOpportunity(requestId: number): Promise<void> {
    await useOneButtonStore.getState().fetchOnebuttonCapabilities(false);
    if (isStale(requestId)) return;

    const caps = useOneButtonStore.getState().onebuttonCapabilities;
    const {
        setOpportunityinfoMasterData,
        setProductTypeList,
        setFinancePartnersList,
    } = useOneButtonStore.getState();

    try {
        const res = await masterServices.GetMasterDataByStep({
            step: 1,
            paramDTOs: [{ key: 'string', value: 'string' }],
        });
        if (isStale(requestId)) return;

        const data = res?.data || res;
        setOpportunityinfoMasterData(data);

        if (data?.ProductType && Array.isArray(data.ProductType)) {
            const productsList: any[] = [];
            data.ProductType.forEach((ele: any) => {
                if (
                    ele.Text === COMMON_CONSTANTS.SolarProductName ||
                    ele.Text === COMMON_CONSTANTS.BatteryProductName ||
                    ele.Text === COMMON_CONSTANTS.RoofProductName ||
                    ele.Text === COMMON_CONSTANTS.RRRProductName ||
                    ele.Text === COMMON_CONSTANTS.RRProduct
                ) {
                    productsList.push(ele);
                }
            });
            setProductTypeList(productsList);
        }

        const rawPartners = data?.FinancePartnerName;
        if (Array.isArray(rawPartners)) {
            let list: typeof rawPartners = rawPartners;
            if (caps?.capabilities) {
                const allowed = buildCapabilityFinancePartnerDisplayNames(caps);
                list =
                    allowed.length > 0
                        ? filterFinancePartnerMasterListByAllowList(rawPartners, allowed)
                        : [];
            }
            setFinancePartnersList(list);
        } else {
            setFinancePartnersList([]);
        }
    } catch (err) {
        console.error('Error fetching global master data:', err);
    }
}

function applyPriceQuoteToStore(data: any): void {
    const {
        setPriceQuoteDetails,
        setPriceQuoteOverrides,
        setSystemDesignQuotesLoading,
        selectedHoverEstimate,
    } = useOneButtonStore.getState();

    const merged = mergeHoverEstimateIntoPriceQuote(data, selectedHoverEstimate) ?? data;
    setPriceQuoteDetails(merged as any);
    const roof = merged?.RoofSquareOverride;
    const normal = merged?.Normal_Slope_Override;
    const low = merged?.Low_Slope_Override;
    const prev = useOneButtonStore.getState().priceQuoteOverrides;
    const nextOverrides = {
        ...prev,
        roofSquareOverride:
            typeof roof === 'number' && Number.isFinite(roof) ? roof : (prev?.roofSquareOverride ?? null),
        normalSlopeOverride:
            typeof normal === 'number' && Number.isFinite(normal)
                ? normal
                : (prev?.normalSlopeOverride ?? null),
        lowSlopeOverride:
            typeof low === 'number' && Number.isFinite(low) ? low : (prev?.lowSlopeOverride ?? null),
    };
    setPriceQuoteOverrides(
        mergeHoverEstimateIntoPriceQuoteOverrides(nextOverrides, selectedHoverEstimate),
    );
    setSystemDesignQuotesLoading(false);
}

async function loadEquipmentMasterIfNeeded(requestId: number, opportunityDetails: any): Promise<void> {
    const solarProduct = opportunityDetails?.OpportunityProduct?.find(
        (p: any) => p.Product_Type_Name === 'Solar',
    );
    const financePartnerId = solarProduct?.Finance_Partner_Id;
    if (!financePartnerId) return;

    try {
        const res = await designServices.GetEquipmentMasterList(financePartnerId);
        if (isStale(requestId)) return;
        useOneButtonStore.getState().setEquipmentMasterData(res?.data || res);
    } catch (err) {
        console.error('Error fetching equipment master data:', err);
    }
}

async function loadTitleInfoIfNeeded(requestId: number, opportunityDetails: any): Promise<void> {
    const directLeadId = opportunityDetails?.DirectLeadID;
    const installationAddress = opportunityDetails?.InstallationAddress;
    if (!directLeadId || !installationAddress) return;

    try {
        const res = await opportunityInfoService.getCustomerTitleInfo(directLeadId, installationAddress);
        if (isStale(requestId)) return;
        useOneButtonStore.getState().setTitleInfo(res?.data?.locations?.[0]);
    } catch (err) {
        console.error('Error fetching customer title info:', err);
    }
}

const OPPORTUNITY_NOT_FOUND_MESSAGE = 'No records found for lead details';

function failOpportunityInitialization(
    requestId: number,
    salesForceId: string,
    message: string,
): void {
    if (isStale(requestId)) return;
    const {
        setOpportunityID,
        setOpportunityDetails,
        setOpportunityLoadError,
        completeOpportunityInitialization,
    } = useOneButtonStore.getState();
    setOpportunityID(null);
    setOpportunityDetails(null);
    setOpportunityLoadError(message);
    completeOpportunityInitialization(salesForceId, message);
}

/**
 * Loads all critical Opportunity Information dashboard data in a single coordinated pass.
 * Populates {@link useOneButtonStore}; callers must check {@link isStale} via requestId.
 */
export async function runOpportunityInitialization({
    salesForceId,
    requestId,
    waitForSiteDesign = true,
}: RunOpportunityInitializationOptions): Promise<void> {
    const {
        setOpportunityID,
        setOpportunityDetails,
        setOpportunityLoadError,
        setSolarUtilityData,
        setSystemDesign,
        setSystemDesignLosses,
        setDesignImage,
        setSystemDesignQuotesLoading,
        completeOpportunityInitialization,
    } = useOneButtonStore.getState();

    let fatalError: string | null = null;

    try {
        if (isStale(requestId)) return;

        setPhase(requestId, 'Loading opportunity details…');
        let actualData: any | null = null;
        try {
            const detailRaw = await opportunityInfoService.getOpportunityDetail(salesForceId);
            if (isStale(requestId)) return;
            actualData = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        } catch (err) {
            if (err instanceof OpportunityDetailNotFoundError) {
                failOpportunityInitialization(requestId, salesForceId, err.message);
                return;
            }
            throw err;
        }

        setOpportunityID(actualData?.Opportunity_Id ?? null);
        setOpportunityDetails(actualData);

        if (actualData == null) {
            failOpportunityInitialization(requestId, salesForceId, OPPORTUNITY_NOT_FOUND_MESSAGE);
            return;
        }

        setOpportunityLoadError(null);

        const opportunityId = actualData?.Opportunity_Id;
        const opportunityIdStr =
            opportunityId != null && String(opportunityId).trim() !== ''
                ? String(opportunityId)
                : null;

        setPhase(requestId, 'Loading utility and product data…');

        const [utilityResult] = await Promise.all([
            opportunityIdStr
                ? designServices
                      .RetrieveUtilityInformation(opportunityIdStr)
                      .catch(() => null)
                : Promise.resolve(null),
            loadMasterDataForOpportunity(requestId),
        ]);
        if (isStale(requestId)) return;

        setSolarUtilityData(utilityResult?.utilityDetails ?? null);

        if (opportunityIdStr) {
            setPhase(requestId, 'Loading system design and pricing…');
            setSystemDesignQuotesLoading(true);

            const [priceQuoteResult, systemDesignBundle, hoverJobIdResult] = await Promise.all([
                quoteServices.getRetrievePriceQuote(opportunityIdStr).catch(() => null),
                Promise.all([
                    designServices.RetrieveSystemDesign(opportunityIdStr).catch(() => null),
                    designServices.SystemDesignLosses(opportunityIdStr).catch(() => null),
                    designServices.GetDesignImage(opportunityIdStr).catch(() => null),
                ]),
                /* Angular side-menu: GET HoverJobID — non-fatal */
                hoverServices.getHoverJobId(opportunityIdStr).catch(() => null),
            ]);
            if (isStale(requestId)) return;

            useOneButtonStore.getState().setHoverJobId(hoverJobIdResult);
            useOneButtonStore.getState().setSelectedHoverEstimate(null);

            if (priceQuoteResult) {
                applyPriceQuoteToStore(priceQuoteResult);
            } else {
                useOneButtonStore.getState().setPriceQuoteDetails(null);
                useOneButtonStore.getState().setPriceQuoteOverrides(null);
                setSystemDesignQuotesLoading(false);
            }

            const [systemDesign, systemDesignLosses, designImage] = systemDesignBundle;
            setSystemDesign(systemDesign ?? null);
            setSystemDesignLosses(systemDesignLosses ?? null);
            setDesignImage(designImage ?? null);

            setPhase(requestId, 'Loading quotes…');
            try {
                await quoteServices.getQuotes(salesForceId);
            } catch {
                /* quotes section handles empty state; non-fatal for page gate */
            }
            if (isStale(requestId)) return;
        }

        if (waitForSiteDesign) {
            await waitForSiteDesignReady(requestId);
            if (isStale(requestId)) return;
        }

        setPhase(requestId, 'Preparing dashboard…');

        if (actualData) {
            void loadEquipmentMasterIfNeeded(requestId, actualData);
            void loadTitleInfoIfNeeded(requestId, actualData);
        }
    } catch (err) {
        console.error('Opportunity initialization failed', err);
        fatalError =
            err instanceof Error
                ? err.message
                : 'Opportunity information could not be loaded. Please try again.';
    }

    if (isStale(requestId)) return;
    completeOpportunityInitialization(salesForceId, fatalError);
}
